import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Breadcrumbs,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  Link,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Icon } from '@iconify/react';
import Loader from '../../../components/loader';
import AlertToast from '../../../components/alertToast';
import {
  countryManagementGetById,
  createAdministrativeLocation,
  listAdministrativeLocations,
  setAdministrativeLocationActive,
  updateAdministrativeLocation,
} from '../countryManagement.service';
import { resultMessage } from '../../../../service/apiResult';
import { FilterSearchField } from '../../../components/filterControls';
import { FieldLabel, formFieldSx, sectionTitleSx } from '../../../styles/formStyles';
import {
  createEditBackButtonSx,
  createEditCardSx,
  createEditPageHeaderSx,
  createEditPageShellSx,
  createEditPageTitleSx,
  createEditPrimaryButtonSx,
} from '../../../styles/pageFormStyles';

const ALERT_DURATION = 3000;

/** `get` hands back the whole axios response; the write verbs hand back the payload already. */
const payloadOf = (result) => result?.data?.data ?? result?.data ?? null;

function CountryLocations() {
  const { id: countryId } = useParams();
  const navigate = useNavigate();

  const [country, setCountry] = useState(null);
  // Each entry is an ancestor the user drilled through. Its length IS the current depth, which is
  // what makes the level of the rows on screen derivable rather than something to track separately.
  const [trail, setTrail] = useState([]);
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);

  const [dialog, setDialog] = useState(null); // { mode: 'create' | 'edit', location }
  const [form, setForm] = useState({ name: '', code: '' });
  const [formError, setFormError] = useState(null);
  const [confirm, setConfirm] = useState(null); // location pending deactivation

  const [alert, setAlert] = useState(null); // { variant, message }

  const levels = useMemo(
    () => [...(country?.administrativeLocationTypes || [])].sort((a, b) => (a.level || 0) - (b.level || 0)),
    [country]
  );

  const currentLevel = trail.length + 1;
  const currentLevelName = levels.find((level) => level.level === currentLevel)?.name || `Level ${currentLevel}`;
  const hasCurrentLevel = levels.some((level) => level.level === currentLevel);
  const hasDeeperLevel = levels.some((level) => level.level === currentLevel + 1);
  const parentId = trail.length ? trail[trail.length - 1].id : null;

  const showAlert = (variant, message) => {
    setAlert({ variant, message });
    setTimeout(() => setAlert(null), ALERT_DURATION);
  };

  const loadCountry = useCallback(async () => {
    const response = await countryManagementGetById(countryId);
    setCountry(response?.data || null);
  }, [countryId]);

  const loadRows = useCallback(async () => {
    setLoading(true);
    const result = await listAdministrativeLocations(countryId, parentId, search);
    setLoading(false);
    if (!result.success) {
      showAlert('error', resultMessage(result, 'Could not load locations.'));
      return;
    }
    setRows(payloadOf(result) || []);
  }, [countryId, parentId, search]);

  useEffect(() => { loadCountry(); }, [loadCountry]);

  // Debounced so typing in the search box does not fire a request per keystroke.
  useEffect(() => {
    const timer = setTimeout(loadRows, 250);
    return () => clearTimeout(timer);
  }, [loadRows]);

  const openCreate = () => {
    setForm({ name: '', code: '' });
    setFormError(null);
    setDialog({ mode: 'create', location: null });
  };

  const openEdit = (location) => {
    setForm({ name: location.name || '', code: location.code || '' });
    setFormError(null);
    setDialog({ mode: 'edit', location });
  };

  const submitDialog = async () => {
    if (!form.name.trim()) {
      setFormError('Name is required.');
      return;
    }

    const payload = {
      name: form.name.trim(),
      code: form.code.trim() || null,
      level: currentLevel,
      parentId,
    };

    setLoading(true);
    const result = dialog.mode === 'create'
      ? await createAdministrativeLocation(countryId, payload)
      : await updateAdministrativeLocation(countryId, dialog.location.id, payload);
    setLoading(false);

    if (!result.success) {
      // Kept inside the dialog rather than shown as a toast: every refusal here names something the
      // operator can fix in the field they are looking at.
      setFormError(resultMessage(result, 'That could not be saved.'));
      return;
    }

    setDialog(null);
    showAlert('success', dialog.mode === 'create' ? `${currentLevelName} added.` : 'Location updated.');
    loadRows();
  };

  const applyActive = async (location, active) => {
    setConfirm(null);
    setLoading(true);
    const result = await setAdministrativeLocationActive(countryId, location.id, active);
    setLoading(false);

    if (!result.success) {
      showAlert('error', resultMessage(result, 'That could not be changed.'));
      return;
    }
    showAlert('success', active
      ? `"${location.name}" is active again.`
      : `"${location.name}" and all locations beneath it were deactivated.`);
    loadRows();
  };

  const drillInto = (location) => {
    setSearch('');
    setTrail((current) => [...current, location]);
  };

  const jumpTo = (index) => {
    setSearch('');
    setTrail((current) => current.slice(0, index));
  };

  return (
    <Box sx={createEditPageShellSx}>
      <Box sx={createEditPageHeaderSx}>
        <Button
          onClick={() => navigate(`/countryManagementView/${countryId}`)}
          startIcon={<Icon icon="mdi:arrow-left" width="20" height="20" />}
          sx={createEditBackButtonSx}
        >
          Back
        </Button>
        <Typography className="app-page-title" sx={createEditPageTitleSx}>
          {country?.name ? `${country.name} — Locations` : 'Locations'}
        </Typography>
      </Box>

      <Box sx={createEditCardSx}>
        <Breadcrumbs sx={{ mb: 2 }} separator={<Icon icon="mdi:chevron-right" width="16" height="16" />}>
          <Link
            component="button"
            type="button"
            underline={trail.length ? 'hover' : 'none'}
            onClick={() => jumpTo(0)}
            sx={{ color: trail.length ? '#294190' : 'text.primary', cursor: 'pointer', fontSize: 14 }}
          >
            {country?.name || 'Country'}
          </Link>
          {trail.map((ancestor, index) => (
            <Link
              key={ancestor.id}
              component="button"
              type="button"
              underline={index === trail.length - 1 ? 'none' : 'hover'}
              onClick={() => jumpTo(index + 1)}
              sx={{
                color: index === trail.length - 1 ? 'text.primary' : '#294190',
                cursor: 'pointer',
                fontSize: 14,
              }}
            >
              {ancestor.name}
            </Link>
          ))}
        </Breadcrumbs>

        <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <Grid size={{ xs: 12, md: 7 }}>
            <Typography sx={sectionTitleSx}>
              {hasCurrentLevel ? `${currentLevelName}s` : 'No further levels'}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {hasCurrentLevel
                ? trail.length
                  ? `Recorded under ${trail[trail.length - 1].name}.`
                  : `Top-level ${currentLevelName.toLowerCase()}s for this country.`
                : 'This country defines no level below the one above. Add one on the country screen to go deeper.'}
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, md: 5 }}>
            <Box sx={{ display: 'flex', gap: 1.5, justifyContent: { md: 'flex-end' } }}>
              <FilterSearchField
                placeholder="Search name or code"
                value={search}
                onChange={setSearch}
                // Shares the row with the Add button, so it takes the slack up to a readable width
                // rather than claiming a whole 320px basis.
                sx={{ flex: 1, maxWidth: 260, minWidth: 0 }}
              />
              <Button
                variant="contained"
                onClick={openCreate}
                disabled={!hasCurrentLevel}
                startIcon={<Icon icon="mdi:plus" width="18" height="18" />}
                sx={{ ...createEditPrimaryButtonSx, whiteSpace: 'nowrap' }}
              >
                Add
              </Button>
            </Box>
          </Grid>
        </Grid>

        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Code</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 600 }} align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={4}>
                  <Typography variant="body2" color="textSecondary" sx={{ py: 3, textAlign: 'center' }}>
                    {search
                      ? 'Nothing matches that search.'
                      : hasCurrentLevel
                        ? `No ${currentLevelName.toLowerCase()}s recorded here yet.`
                        : 'Nothing to show.'}
                  </Typography>
                </TableCell>
              </TableRow>
            )}

            {rows.map((location) => (
              <TableRow key={location.id} hover>
                <TableCell>
                  {hasDeeperLevel && location.active ? (
                    <Link
                      component="button"
                      type="button"
                      onClick={() => drillInto(location)}
                      sx={{ color: '#294190', cursor: 'pointer', fontSize: 14, textAlign: 'left' }}
                    >
                      {location.name}
                    </Link>
                  ) : (
                    <Typography variant="body2">{location.name}</Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="textSecondary">{location.code || '-'}</Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    variant="outlined"
                    color={location.active ? 'success' : 'default'}
                    label={location.active ? 'Active' : 'Inactive'}
                  />
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="Rename">
                    <IconButton size="small" onClick={() => openEdit(location)}>
                      <Icon icon="mdi:pencil-outline" width="18" height="18" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={location.active ? 'Deactivate' : 'Reactivate'}>
                    <IconButton
                      size="small"
                      onClick={() => (location.active ? setConfirm(location) : applyActive(location, true))}
                    >
                      <Icon
                        icon={location.active ? 'mdi:eye-off-outline' : 'mdi:eye-outline'}
                        width="18"
                        height="18"
                      />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>

      <Dialog open={Boolean(dialog)} onClose={() => setDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>
          {dialog?.mode === 'create' ? `Add ${currentLevelName}` : `Edit ${currentLevelName}`}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            <FieldLabel label="Name" required />
            <TextField
              autoFocus
              fullWidth
              value={form.name}
              onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))}
              sx={formFieldSx}
            />
          </Box>
          <Box sx={{ mt: 2 }}>
            <FieldLabel label="Code" />
            <TextField
              fullWidth
              value={form.code}
              placeholder="Optional"
              onChange={(event) => setForm((current) => ({ ...current, code: event.target.value }))}
              sx={formFieldSx}
            />
            <Typography variant="caption" color="textSecondary">
              An identifier from an official list, if there is one. Leave empty otherwise.
            </Typography>
          </Box>
          {dialog?.mode === 'edit' && (
            <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 2 }}>
              Renaming does not move this location. Everything already recorded against it — farmers,
              land, offices — keeps pointing at it under its new name.
            </Typography>
          )}
          {formError && (
            <Typography variant="caption" color="error" sx={{ display: 'block', mt: 2 }}>
              {formError}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setDialog(null)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button onClick={submitDialog} variant="contained" sx={{ textTransform: 'none', borderRadius: '10px' }}>
            {dialog?.mode === 'create' ? 'Add' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(confirm)} onClose={() => setConfirm(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>Deactivate "{confirm?.name}"?</DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            <Typography variant="body2" sx={{ mb: 1.5 }}>
              It disappears from every address picker, so nothing new can be recorded against it.
              Every active location beneath it — districts, blocks, villages, and so on — is
              deactivated at the same time.
            </Typography>
            <Typography variant="body2">
              Anything already recorded there — farmers, land parcels, offices, coverage areas — is
              left exactly as it is and stays readable. This can be undone.
            </Typography>
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setConfirm(null)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button
            onClick={() => applyActive(confirm, false)}
            variant="contained"
            color="error"
            sx={{ textTransform: 'none', borderRadius: '10px' }}
          >
            Deactivate
          </Button>
        </DialogActions>
      </Dialog>

      {loading && <Loader />}

      <Box textAlign="end">
        <AlertToast
          open={Boolean(alert)}
          onClose={() => setAlert(null)}
          message={alert?.message || ''}
          variant={alert?.variant || 'success'}
        />
      </Box>
    </Box>
  );
}

export default CountryLocations;
